import { useAuthContext } from '@/auth/useAuthContext';
import {
  ApiError,
  getTipSettings,
  postConnectOnboardingLink,
  updateAccount,
  updateTipSettings,
} from '@/lib/api';
import type { UpdateAccountInput } from '@/lib/types';
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  ExternalLink,
  Info,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  NZD: '$',
};

function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

/**
 * Major-currency string → minor integer.
 * - Empty string → null (clears the threshold).
 * - Otherwise: parse as a number, multiply by 100, round to nearest integer.
 * - Any non-finite or negative input returns undefined (caller treats as
 *   "do not patch this field").
 */
function thresholdMajorToMinor(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return undefined;
  return Math.round(parsed * 100);
}

function thresholdMinorToMajor(minor: number | null): string {
  if (minor === null) return '';
  return (minor / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PaymentsSettings() {
  const { state, refresh } = useAuthContext();
  const account = state.me?.activeAccount ?? null;

  // Connect-flow state
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  // Platform Stripe configuration status (probed on mount via a dry POST
  // with a deliberate dummy param; a 503 billing_not_configured response
  // tells us the platform key is absent without actually starting onboarding).
  const [platformConfigured, setPlatformConfigured] = useState<boolean | null>(null);

  // Tax-inclusive toggle
  const [taxInclusive, setTaxInclusive] = useState<boolean>(true);
  const [taxSaving, setTaxSaving] = useState(false);
  const [taxError, setTaxError] = useState<string | null>(null);
  const [taxSuccess, setTaxSuccess] = useState(false);

  // Opt-in Tax/VAT engine (migration 0088)
  const [taxEnabled, setTaxEnabled] = useState<boolean>(false);
  const [taxLabel, setTaxLabel] = useState<string>('VAT');
  const [taxRatePct, setTaxRatePct] = useState<string>('');
  const [taxRegNo, setTaxRegNo] = useState<string>('');
  const [taxEngineSaving, setTaxEngineSaving] = useState(false);
  const [taxEngineError, setTaxEngineError] = useState<string | null>(null);
  const [taxEngineSuccess, setTaxEngineSuccess] = useState(false);

  // Tips (migration 0098) — 1:1 side table, loaded separately from the account.
  const [tipsEnabled, setTipsEnabled] = useState(false);
  const [tipPresetsInput, setTipPresetsInput] = useState('10, 15, 20');
  const [allowCustomTip, setAllowCustomTip] = useState(true);
  const [tipsLoaded, setTipsLoaded] = useState(false);
  const [tipsSaving, setTipsSaving] = useState(false);
  const [tipsError, setTipsError] = useState<string | null>(null);
  const [tipsSuccess, setTipsSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getTipSettings();
        if (cancelled) return;
        setTipsEnabled(res.settings.tipsEnabled);
        setTipPresetsInput(res.settings.tipPresets.join(', '));
        setAllowCustomTip(res.settings.allowCustomTip);
        setTipsLoaded(true);
      } catch {
        // Endpoint unavailable (or mocked away in tests) — keep defaults, still editable.
        if (!cancelled) setTipsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleTipsSave() {
    setTipsSaving(true);
    setTipsError(null);
    setTipsSuccess(false);
    const presets = tipPresetsInput
      .split(/[,\s]+/)
      .map((p) => p.trim().replace('%', ''))
      .filter((p) => p.length > 0)
      .map((p) => Number.parseInt(p, 10));
    if (
      presets.length === 0 ||
      presets.length > 4 ||
      presets.some((p) => !Number.isInteger(p) || p < 1 || p > 100)
    ) {
      setTipsError('Enter between one and four whole-number percentages, e.g. 10, 15, 20.');
      setTipsSaving(false);
      return;
    }
    try {
      const res = await updateTipSettings({ tipsEnabled, tipPresets: presets, allowCustomTip });
      setTipsEnabled(res.settings.tipsEnabled);
      setTipPresetsInput(res.settings.tipPresets.join(', '));
      setAllowCustomTip(res.settings.allowCustomTip);
      setTipsSuccess(true);
    } catch (err) {
      setTipsError(
        err instanceof ApiError ? `Could not save (${err.code}).` : 'Could not save tip settings.',
      );
    } finally {
      setTipsSaving(false);
    }
  }

  // Refund preferences
  const [refundDefault, setRefundDefault] = useState<'card' | 'credit'>('card');
  const [refundThreshold, setRefundThreshold] = useState<string>('');
  const [refundSaving, setRefundSaving] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [refundSuccess, setRefundSuccess] = useState(false);

  // Sync local state with the active account whenever it loads / refreshes.
  useEffect(() => {
    if (account) {
      setTaxInclusive(account.taxInclusive === 1);
      setRefundDefault(account.refundDefault === 'credit' ? 'credit' : 'card');
      setRefundThreshold(thresholdMinorToMajor(account.refundAutoApproveThresholdMinor));
      // Opt-in tax engine.
      setTaxEnabled(account.taxEnabled === 1);
      setTaxLabel(account.taxLabel ?? 'VAT');
      setTaxRatePct(
        account.taxRateBps != null && account.taxRateBps > 0
          ? (account.taxRateBps / 100).toString()
          : '',
      );
      setTaxRegNo(account.taxRegistrationNumber ?? '');
    }
  }, [account]);

  // Probe platform Stripe configuration on mount. We call the onboarding-link
  // endpoint and look for a 503 billing_not_configured; any other response
  // (including 200, 403, network errors) means Stripe is wired at the platform
  // level. This avoids adding a separate /status endpoint.
  useEffect(() => {
    fetch('/api/billing/connect/onboarding-link', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ __probe: true }),
    })
      .then((res) => {
        if (res.status === 503) {
          return res.json().then((body: unknown) => {
            const err = (body as Record<string, unknown>)?.error;
            setPlatformConfigured(err !== 'billing_not_configured');
          });
        }
        // 200, 403, or any non-503 → platform key is present
        setPlatformConfigured(true);
      })
      .catch(() => {
        // Network error — assume configured and surface real errors on connect click
        setPlatformConfigured(true);
      });
  }, []);

  if (state.loading) {
    return (
      <div className="mx-auto max-w-[720px] px-6 py-10 md:px-10 md:py-14">
        <div className="space-y-3">
          <div className="h-8 w-48 animate-pulse rounded bg-secondary" />
          <div className="h-4 w-full animate-pulse rounded bg-secondary" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-secondary" />
        </div>
      </div>
    );
  }

  if (!account) {
    return <p className="text-sm text-muted-foreground">No active account.</p>;
  }

  const chargesEnabled = account.stripeChargesEnabled === 1;
  const payoutsEnabled = account.stripePayoutsEnabled === 1;
  const hasStripeAccount = account.stripeAccountId !== null;
  const fullyConnected = hasStripeAccount && chargesEnabled;

  async function handleConnect() {
    setConnectError(null);
    setConnectLoading(true);
    try {
      const { url } = await postConnectOnboardingLink();
      window.location.href = url;
    } catch (err) {
      if (err instanceof ApiError && err.code === 'billing_not_configured') {
        setConnectError(
          'Payments setup unavailable — your operator has not configured Stripe yet. Contact support to enable payments.',
        );
      } else {
        const code = err instanceof ApiError ? err.code : 'unknown';
        setConnectError(`Could not start Stripe onboarding (${code})`);
      }
      setConnectLoading(false);
    }
  }

  async function handleSaveTaxInclusive(next: boolean) {
    setTaxError(null);
    setTaxSuccess(false);
    setTaxSaving(true);
    try {
      await updateAccount({ taxInclusive: next });
      await refresh();
      setTaxSuccess(true);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unknown';
      setTaxError(`Could not save (${code})`);
      // Roll back the visual toggle.
      setTaxInclusive(!next);
    } finally {
      setTaxSaving(false);
    }
  }

  async function handleSaveTaxEngine() {
    setTaxEngineError(null);
    setTaxEngineSuccess(false);

    // Convert the percentage input to basis points (Math.round so 20 → 2000,
    // 8.875 → 888). Empty/zero is allowed (zero-rated).
    let rateBps = 0;
    const trimmedPct = taxRatePct.trim();
    if (trimmedPct !== '') {
      const parsed = Number(trimmedPct);
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
        setTaxEngineError('Rate must be a percentage between 0 and 100.');
        return;
      }
      rateBps = Math.round(parsed * 100);
    }

    const patch: UpdateAccountInput = {
      taxEnabled,
      taxLabel: taxLabel.trim() || null,
      taxRateBps: rateBps,
      taxRegistrationNumber: taxRegNo.trim() || null,
    };

    setTaxEngineSaving(true);
    try {
      await updateAccount(patch);
      await refresh();
      setTaxEngineSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          err.body && typeof err.body === 'object' && Array.isArray(err.body.details)
            ? (err.body.details as string[]).join('; ')
            : err.code;
        setTaxEngineError(detail);
      } else {
        setTaxEngineError('Something went wrong. Please try again.');
      }
    } finally {
      setTaxEngineSaving(false);
    }
  }

  async function handleSaveRefunds() {
    setRefundError(null);
    setRefundSuccess(false);

    const minor = thresholdMajorToMinor(refundThreshold);
    if (minor === undefined) {
      setRefundError('Threshold must be a non-negative number, or empty to clear.');
      return;
    }

    const patch: UpdateAccountInput = {
      refundDefault,
      refundAutoApproveThresholdMinor: minor,
    };

    setRefundSaving(true);
    try {
      await updateAccount(patch);
      await refresh();
      setRefundSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          err.body && typeof err.body === 'object' && Array.isArray(err.body.details)
            ? (err.body.details as string[]).join('; ')
            : err.code;
        setRefundError(detail);
      } else {
        setRefundError('Something went wrong. Please try again.');
      }
    } finally {
      setRefundSaving(false);
    }
  }

  const symbol = currencySymbol(account.currency);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Payments</h1>
      </header>

      <div className="space-y-6">
        {/* ---------- Stripe Connect status ---------- */}
        <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="border-b border-border px-6 py-4">
            <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
              <Banknote className="size-4 shrink-0" strokeWidth={1.75} />
              Stripe Connect
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Take card payments from your customers via your own Stripe account.
            </p>
          </div>

          <div className="space-y-4 px-6 py-5">
            {/* Platform Stripe not configured banner */}
            {platformConfigured === false && (
              <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                <Info
                  className="mt-0.5 size-4 shrink-0 text-blue-600"
                  strokeWidth={1.75}
                  aria-hidden
                />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Platform Stripe not configured
                  </p>
                  <p className="mt-0.5 text-sm text-blue-800">
                    The kennel.app platform hasn&rsquo;t been connected to Stripe yet. Contact the
                    platform administrator to enable payments.
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <ConnectChip
                label="Charges enabled"
                enabled={chargesEnabled}
                hasAccount={hasStripeAccount}
              />
              <ConnectChip
                label="Payouts enabled"
                enabled={payoutsEnabled}
                hasAccount={hasStripeAccount}
              />
            </div>

            {!hasStripeAccount && (
              <p className="text-sm text-muted-foreground">
                Connect your Stripe account to start taking payments. We use Stripe Connect Standard
                — you keep full control of your account, and we charge no transaction fees on top of
                Stripe&rsquo;s.
              </p>
            )}

            {hasStripeAccount && !chargesEnabled && (
              <p className="text-sm text-muted-foreground">
                Stripe must complete KYC before charges can be processed. Resume onboarding to
                finish providing the required information.
              </p>
            )}

            {connectError && (
              <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {connectError}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3">
              {!hasStripeAccount && (
                <button
                  type="button"
                  onClick={() => void handleConnect()}
                  disabled={connectLoading || platformConfigured === false}
                  title={
                    platformConfigured === false
                      ? 'Platform Stripe not configured — contact the platform administrator'
                      : undefined
                  }
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
                >
                  {connectLoading ? 'Redirecting…' : 'Connect with Stripe'}
                </button>
              )}

              {hasStripeAccount && !chargesEnabled && (
                <button
                  type="button"
                  onClick={() => void handleConnect()}
                  disabled={connectLoading || platformConfigured === false}
                  title={
                    platformConfigured === false
                      ? 'Platform Stripe not configured — contact the platform administrator'
                      : undefined
                  }
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
                >
                  <RefreshCw className="size-3.5" strokeWidth={1.75} />
                  {connectLoading ? 'Redirecting…' : 'Resume Stripe onboarding'}
                </button>
              )}

              {fullyConnected && account.stripeAccountId && (
                <a
                  href={`https://dashboard.stripe.com/${account.stripeAccountId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                >
                  Open Stripe dashboard
                  <ExternalLink className="size-3.5" strokeWidth={1.75} />
                </a>
              )}
            </div>
          </div>
        </section>

        {/* ---------- Tax warning ---------- */}
        {chargesEnabled && (
          <section className="rounded-2xl border border-warning bg-warning/5 p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" strokeWidth={1.75} />
              <div className="flex-1">
                <h2 className="text-base font-semibold text-foreground">
                  Stripe Tax not configured
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Stripe Tax is not configured on your Stripe account — customer invoices
                  won&rsquo;t show VAT lines until you set it up in your Stripe dashboard.
                </p>
                <a
                  href="https://dashboard.stripe.com/settings/tax"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  Configure Stripe Tax
                  <ExternalLink className="size-3.5" strokeWidth={1.75} />
                </a>
              </div>
            </div>
          </section>
        )}

        {/* ---------- Tax-inclusive toggle ---------- */}
        <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-base font-semibold text-foreground">Pricing display</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              How prices appear to customers across booking pages, invoices, and receipts.
            </p>
          </div>

          <div className="px-6 py-5">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={taxInclusive}
                disabled={taxSaving}
                onChange={(e) => {
                  const next = e.target.checked;
                  setTaxInclusive(next);
                  void handleSaveTaxInclusive(next);
                }}
                className="mt-1 size-4 cursor-pointer rounded border-border accent-primary"
              />
              <span className="flex-1">
                <span className="block text-sm font-medium text-foreground">
                  Show tax-inclusive prices to customers
                </span>
                <span className="block text-xs text-muted-foreground">
                  When enabled, prices shown to customers include VAT/tax. When disabled, tax is
                  added at checkout.
                </span>
              </span>
            </label>

            {taxError && <p className="mt-3 text-sm text-destructive">{taxError}</p>}
            {taxSuccess && <p className="mt-3 text-sm font-medium text-success">Saved.</p>}
          </div>
        </section>

        {/* ---------- Tax / VAT engine (opt-in) ---------- */}
        <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-base font-semibold text-foreground">Tax / VAT</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Charge tax on bookings and point-of-sale. When off (the default), no tax is added and
              totals are unchanged. When on, tax is computed for quotes, invoices, and receipts
              using the rate below.
            </p>
          </div>

          <div className="space-y-5 px-6 py-5">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={taxEnabled}
                disabled={taxEngineSaving}
                onChange={(e) => setTaxEnabled(e.target.checked)}
                className="mt-1 size-4 cursor-pointer rounded border-border accent-primary"
              />
              <span className="flex-1">
                <span className="block text-sm font-medium text-foreground">Enable tax</span>
                <span className="block text-xs text-muted-foreground">
                  Turn this on to collect VAT / GST / Sales Tax. Leave off if you are not
                  tax-registered.
                </span>
              </span>
            </label>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="tax-label"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Tax label
                </label>
                <input
                  id="tax-label"
                  type="text"
                  value={taxLabel}
                  disabled={!taxEnabled || taxEngineSaving}
                  onChange={(e) => setTaxLabel(e.target.value)}
                  placeholder="VAT"
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Shown on receipts, e.g. VAT, GST, Sales Tax.
                </p>
              </div>

              <div>
                <label
                  htmlFor="tax-rate"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Rate
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="tax-rate"
                    type="text"
                    inputMode="decimal"
                    value={taxRatePct}
                    disabled={!taxEnabled || taxEngineSaving}
                    onChange={(e) => setTaxRatePct(e.target.value)}
                    placeholder="20"
                    className="num w-28 rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
                  />
                  <span className="num text-sm text-muted-foreground">%</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  e.g. 20 for 20%. Set per-service overrides under each service.
                </p>
              </div>
            </div>

            <fieldset disabled={!taxEnabled || taxEngineSaving}>
              <legend className="mb-2 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Prices include tax?
              </legend>
              <p className="mb-2 text-xs text-muted-foreground">
                This uses the “Show tax-inclusive prices” setting above. When inclusive, your prices
                already contain tax (we show the portion); when exclusive, tax is added on top of
                your prices.
              </p>
              <p className="text-sm text-foreground">
                Currently:{' '}
                <span className="font-medium">
                  {taxInclusive ? 'Inclusive (prices include tax)' : 'Exclusive (tax added on top)'}
                </span>
              </p>
            </fieldset>

            <div>
              <label
                htmlFor="tax-reg-no"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Registration number
              </label>
              <input
                id="tax-reg-no"
                type="text"
                value={taxRegNo}
                disabled={!taxEnabled || taxEngineSaving}
                onChange={(e) => setTaxRegNo(e.target.value)}
                placeholder="e.g. GB123456789"
                className="w-full max-w-xs rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Printed on receipts. Required on VAT invoices in many jurisdictions.
              </p>
            </div>

            {taxEngineError && <p className="text-sm text-destructive">{taxEngineError}</p>}
            {taxEngineSuccess && (
              <p className="text-sm font-medium text-success">Tax settings saved.</p>
            )}

            <div>
              <button
                type="button"
                onClick={() => void handleSaveTaxEngine()}
                disabled={taxEngineSaving}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
              >
                {taxEngineSaving ? 'Saving…' : 'Save tax settings'}
              </button>
            </div>
          </div>
        </section>

        {/* ---------- Tips (migration 0098) ---------- */}
        <section
          className="rounded-2xl bg-surface ring-1 ring-border elevate"
          data-testid="tips-section"
        >
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-base font-semibold text-foreground">Tips</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Offer a gratuity prompt on card payments at the till and on text-to-pay links. Off by
              default. Tips are recorded separately from sales and never count as service revenue.
            </p>
          </div>
          <div className="space-y-5 px-6 py-5">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={tipsEnabled}
                onChange={(e) => setTipsEnabled(e.target.checked)}
                disabled={!tipsLoaded || tipsSaving}
                className="mt-1 size-4 cursor-pointer accent-primary"
                data-testid="tips-enabled"
              />
              <span className="flex-1">
                <span className="block text-sm font-medium text-foreground">Prompt for tips</span>
                <span className="block text-xs text-muted-foreground">
                  Customers see the preset percentages below (and an optional custom amount).
                </span>
              </span>
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="tip-presets"
                  className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Preset percentages
                </label>
                <input
                  id="tip-presets"
                  type="text"
                  value={tipPresetsInput}
                  onChange={(e) => setTipPresetsInput(e.target.value)}
                  disabled={!tipsLoaded || tipsSaving}
                  placeholder="10, 15, 20"
                  className="num w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
                <p className="mt-1 text-xs text-muted-foreground">Up to four, comma-separated.</p>
              </div>
              <label className="flex items-start gap-3 sm:pt-6">
                <input
                  type="checkbox"
                  checked={allowCustomTip}
                  onChange={(e) => setAllowCustomTip(e.target.checked)}
                  disabled={!tipsLoaded || tipsSaving}
                  className="mt-1 size-4 cursor-pointer accent-primary"
                />
                <span className="text-sm text-foreground">Allow a custom tip amount</span>
              </label>
            </div>
            {tipsError && <p className="text-sm text-destructive">{tipsError}</p>}
            {tipsSuccess && <p className="text-sm text-success">Tip settings saved.</p>}
            <div>
              <button
                type="button"
                onClick={() => void handleTipsSave()}
                disabled={!tipsLoaded || tipsSaving}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
                data-testid="tips-save"
              >
                {tipsSaving ? 'Saving…' : 'Save tip settings'}
              </button>
            </div>
          </div>
        </section>

        {/* ---------- Refund preferences ---------- */}
        <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-base font-semibold text-foreground">Refunds</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Default behaviour when a refund is requested for a booking.
            </p>
          </div>

          <div className="space-y-5 px-6 py-5">
            <fieldset>
              <legend className="mb-2 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Default refund destination
              </legend>
              <div className="space-y-2">
                <label className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="refundDefault"
                    value="card"
                    checked={refundDefault === 'card'}
                    onChange={() => setRefundDefault('card')}
                    className="mt-1 size-4 cursor-pointer accent-primary"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-foreground">
                      Refund to original card
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Money goes back to the customer&rsquo;s original payment method.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="refundDefault"
                    value="credit"
                    checked={refundDefault === 'credit'}
                    onChange={() => setRefundDefault('credit')}
                    className="mt-1 size-4 cursor-pointer accent-primary"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-foreground">
                      Issue as account credit
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Refund stays on the customer&rsquo;s account for future bookings.
                    </span>
                  </span>
                </label>
              </div>
            </fieldset>

            <div>
              <label
                htmlFor="refund-threshold"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Auto-approve refunds under
              </label>
              <div className="flex items-center gap-2">
                <span className="num text-sm text-muted-foreground">{symbol}</span>
                <input
                  id="refund-threshold"
                  type="text"
                  inputMode="decimal"
                  value={refundThreshold}
                  onChange={(e) => setRefundThreshold(e.target.value)}
                  placeholder="20.00"
                  className="num w-32 rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Leave blank to require staff approval on every refund.
              </p>
            </div>

            {refundError && <p className="text-sm text-destructive">{refundError}</p>}
            {refundSuccess && (
              <p className="text-sm font-medium text-success">Refund preferences saved.</p>
            )}

            <div>
              <button
                type="button"
                onClick={() => void handleSaveRefunds()}
                disabled={refundSaving}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
              >
                {refundSaving ? 'Saving…' : 'Save refund preferences'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

interface ConnectChipProps {
  label: string;
  enabled: boolean;
  hasAccount: boolean;
}

function ConnectChip({ label, enabled, hasAccount }: ConnectChipProps) {
  if (enabled) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success">
        <CheckCircle2 className="size-3.5" strokeWidth={2} />
        {label}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground"
      title={hasAccount ? 'Stripe must complete KYC before this is enabled' : undefined}
    >
      <XCircle className="size-3.5" strokeWidth={2} />
      {label}
    </span>
  );
}
